// =============================================================================
// catalog-client.ts — Client API LECTURE du BC catalog (S8-D2), DOUBLABLE.
// =============================================================================
// Miroir des endpoints de LECTURE du BC catalog (apps/api/src/catalog/...).
// Comme les autres clients UI (suppliers, clients, legal, parameters), expose des
// INTERFACES consommées par les hooks TanStack ; l'impl HTTP réelle (fetch) viendra
// derrière la MÊME interface sans toucher l'UI (stub-first).
//
// `catalog` est un référentiel GLOBAL (DDD §3 BC catalog) : AUCUNE donnée nominative,
// AUCUNE PII. Les DTO ci-dessous ne portent QUE des métadonnées de version (id,
// numéro, état, dates). L'AUTORITÉ (RBAC admin vs supplier, transitions d'état
// DRAFT→PUBLISHED→ARCHIVED, figement, publication) est l'API — l'UI relaie l'intention
// de LECTURE seulement. Aucune écriture ici (édition/upload/figement = S9/S11, hors D2).
//
// DTO = miroirs HTTP (PAS le Published Language, PAS @jeko/db) : l'enum d'état est
// répliqué en union de littéraux ASCII (parité contrat API figé S8-D2). La SPA ne
// couple PAS au paquet DB.

import { HttpApiError } from '@/api/http-error';

// --- A-08 (admin) — browser des versions du catalogue (lecture seule) ---

/**
 * État d'une version de catalogue (parité contrat API figé S8-D2). ASCII, valeurs
 * du Published Language ; le libellé FR accentué est rendu côté UI (i18n front).
 */
export type CatalogVersionStateDto = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

/**
 * Ligne du browser des versions (A-08, admin). Métadonnées de version UNIQUEMENT —
 * aucune donnée nominative, aucune PII (catalog = référentiel GLOBAL).
 * `publishedAt`/`archivedAt` sont null tant que la transition correspondante n'a pas
 * eu lieu (autorité = API).
 */
export interface CatalogVersionDto {
  readonly catalogVersionId: string;
  readonly versionNumber: string;
  readonly state: CatalogVersionStateDto;
  /** Horodatage ISO 8601 de création. */
  readonly createdAt: string;
  /** Horodatage ISO 8601 de publication, null si jamais publiée. */
  readonly publishedAt: string | null;
  /** Horodatage ISO 8601 d'archivage, null si non archivée. */
  readonly archivedAt: string | null;
}

// --- F-05 (fournisseur) — version PUBLISHED courante (lecture seule) ---

/**
 * Version publiée courante exposée au fournisseur (F-05). Sous-ensemble strict des
 * métadonnées (le fournisseur ne voit ni l'état brut, ni les brouillons/archives) ;
 * aucune PII. `null` (au niveau du résultat de l'appel) = aucune version publiée.
 */
export interface CurrentCatalogVersionDto {
  readonly catalogVersionId: string;
  readonly versionNumber: string;
  /** Horodatage ISO 8601 de publication, null si l'API ne le renseigne pas encore. */
  readonly publishedAt: string | null;
}

/**
 * Erreur applicative renvoyée par l'API catalog (HTTP 4xx/5xx). L'UI l'affiche telle
 * quelle ; elle ne réinterprète jamais la règle métier (autorité = API).
 *
 * `code` = code machine STABLE du corps d'erreur structuré NestJS (`{ statusCode, code,
 * message }`) quand l'API en fournit un — ex. A-CATALOG §2.4 archivage :
 * `CATALOG_VERSION_WRONG_STATE` (version ≠ DRAFT) / `PROFILE_ALREADY_ARCHIVED` (idempotence).
 * `undefined` pour les erreurs sans code (500 générique, proxy). L'UI branche sur le `code`
 * (pas sur le message, non stable) pour afficher un libellé métier précis.
 */
export class CatalogApiError extends HttpApiError {
  public readonly code: string | undefined;

  public constructor(message: string, code?: string, status?: number) {
    super(message, status, 'CatalogApiError');
    this.code = code;
  }
}

// --- A-CATALOG §2.4 — codes d'erreur STABLES de l'archivage profil (parité back) ---

/**
 * Codes machine des refus d'archivage de profil (CatalogEditionController.mapArchiveErrors) :
 *  • `CATALOG_VERSION_WRONG_STATE` (409) : la version n'est pas DRAFT (édition/archivage refusé).
 *  • `PROFILE_ALREADY_ARCHIVED` (409) : le profil est DÉJÀ archivé (idempotence).
 * L'UI mappe ces codes vers un libellé métier ; le message serveur reste le repli.
 */
export const CATALOG_VERSION_WRONG_STATE = 'CATALOG_VERSION_WRONG_STATE';
export const PROFILE_ALREADY_ARCHIVED = 'PROFILE_ALREADY_ARCHIVED';
/**
 * Code machine du refus de RESTAURATION d'un profil non archivé (DBU §2.4 l.960, garde R3-inverse) :
 *  • `PROFILE_NOT_ARCHIVED` (409) : le profil ciblé est déjà Actif (restauration inapplicable).
 */
export const PROFILE_NOT_ARCHIVED = 'PROFILE_NOT_ARCHIVED';
/**
 * Code machine du refus d'ÉDITION du descriptif d'un profil ARCHIVÉ (DBU §2.4 l.957, GAP-24-A) :
 *  • `PROFILE_ARCHIVED_NOT_EDITABLE` (409) : la modification du descriptif est réservée aux profils
 *    ACTIFS ; un profil archivé doit être RESTAURÉ avant toute édition. L'UI masque « Modifier » sur un
 *    profil archivé (garde côté client) ; ce code couvre le refus serveur pour toute soumission résiduelle.
 */
export const PROFILE_ARCHIVED_NOT_EDITABLE = 'PROFILE_ARCHIVED_NOT_EDITABLE';

// --- R15-CAT-01 §2.4 — codes d'erreur STABLES de la FUSION de profils (parité back) ---

/**
 * Codes machine des refus de FUSION de profils (CatalogEditionController.mapMergeErrors) :
 *  • `PROFILE_MERGE_SAME_PROFILE` (400) : source === cible (deux profils distincts requis).
 *  • `PROFILE_MERGE_CONFIRMATION_MISMATCH` (400) : la confirmation textuelle ≠ code source.
 *  • `PROFILE_MERGE_OPERAND_ARCHIVED` (409) : la source OU la cible est archivée (les deux doivent être actifs).
 * `CATALOG_VERSION_WRONG_STATE` (409, version ≠ DRAFT) est partagé avec l'archivage. L'UI mappe ces codes
 * vers un libellé métier ; le message serveur reste le repli.
 */
export const PROFILE_MERGE_SAME_PROFILE = 'PROFILE_MERGE_SAME_PROFILE';
export const PROFILE_MERGE_CONFIRMATION_MISMATCH = 'PROFILE_MERGE_CONFIRMATION_MISMATCH';
export const PROFILE_MERGE_OPERAND_ARCHIVED = 'PROFILE_MERGE_OPERAND_ARCHIVED';

// --- A-25 §2.10 — codes d'erreur STABLES du cycle de vie compétence (parité back, #4/#16) ---

/**
 * Code machine du refus d'ÉDITION d'une compétence ARCHIVÉE (DBU §2.10 l.2222, #4 ; parité
 * PROFILE_ARCHIVED_NOT_EDITABLE) :
 *  • `SKILL_ARCHIVED_NOT_EDITABLE` (409) : la modification (contenu OU familles) est réservée aux
 *    compétences ACTIVES ; une compétence archivée doit être RESTAURÉE avant toute édition. L'UI masque
 *    « Modifier » sur une compétence archivée ; ce code couvre le refus serveur résiduel.
 */
export const SKILL_ARCHIVED_NOT_EDITABLE = 'SKILL_ARCHIVED_NOT_EDITABLE';
/**
 * Code machine du refus de RESTAURATION d'une compétence non archivée (DBU §2.10 l.2256, #16, garde
 * inverse ; parité PROFILE_NOT_ARCHIVED) :
 *  • `SKILL_NOT_ARCHIVED` (409) : la compétence ciblée est déjà Active (restauration inapplicable).
 */
export const SKILL_NOT_ARCHIVED = 'SKILL_NOT_ARCHIVED';

/**
 * Contrat du client catalog ADMIN — miroir de l'endpoint de LECTURE A-08. Aucune
 * règle métier ici : l'autorité (RBAC AdminSessionGuard, transitions d'état) est
 * l'API. L'UI relaie l'intention de lecture.
 */
export interface CatalogAdminClient {
  /** GET /admin/catalog/versions — browser des versions du catalogue (A-08, gardé admin). */
  listVersions(): Promise<readonly CatalogVersionDto[]>;
}

// =============================================================================
// S9-D4 — ÉCRITURE ADMIN (A-09 profil + matrice + A-25 compétences + A-10 upload).
// =============================================================================
// P-05 (séparation stricte par écrans/routes DISTINCTS) : ces clients d'ÉCRITURE
// admin sont DISTINCTS des lectures S8 (A-08 admin browser, F-05 fournisseur). Mondes
// séparés ; ils restent dans le MONDE ADMIN (le DI seam admin auto-porté les injecte).
//
// DTO = miroirs HTTP des DTO FIGÉS back (apps/api/src/catalog/dto/catalog-edit.dto.ts) :
// types stables, sérialisables, ISO 8601, AUCUNE fuite ORM. `catalog` est GLOBAL
// admin-only : AUCUNE PII (référentiel structurel). L'AUTORITÉ (inv.1 unicité perpétuelle,
// inv.2 édition réservée DRAFT, poids ∈ [0,1], inv.7 promotion bloquée si anomalie
// bloquante, dry-run SANS création de version) est l'API ; l'UI relaie l'intention.

// --- A-09 édition profil (DDD §3.4.4) — version DRAFT uniquement ---

/** Niveaux de séniorité (énumération fermée DDD §3.4.6, parité back). */
export type SeniorityLevelDto = 'Débutant' | 'Maîtrisant' | 'Expert' | 'Référent';

/** État d'un Profile (parité enum back `ProfileStateDto`). */
export type ProfileStateDto = 'Active' | 'Archived';

/**
 * Compétences de référence PAR niveau (DBU §2.4 l.954 ; F0-b JSONB, parité back `CompetencesByLevelDto`).
 * Enregistrement PARTIEL : une clé n'est présente que si le niveau porte ≥ 1 compétence. Clés ⊆
 * `availableLevels` (cohérence validée par l'API).
 */
export type CompetencesByLevelDto = Partial<Record<SeniorityLevelDto, readonly string[]>>;

/**
 * Écriture d'un Profile dans une version DRAFT (upsert édition A-09). `profileCode`
 * absent ⇒ création (le service alloue + vérifie inv.1) ; présent ⇒ mise à jour.
 */
export interface EditProfileInputDto {
  readonly profileCode?: string;
  readonly libelle: string;
  readonly famille: string;
  readonly sousFamille: string;
  readonly description: string;
  readonly availableLevels: readonly SeniorityLevelDto[];
  /** Contextes d'intervention (DBU §2.4 l.941). OPTIONNEL, texte riche. `null` ⇒ vide. */
  readonly contextes?: string | null;
  /** Environnements technologiques associés (DBU §2.4 l.941). OPTIONNEL, texte riche. `null` ⇒ vide. */
  readonly environnements?: string | null;
  /** Descriptif de missions-type (DBU §2.4 l.950/952). OPTIONNEL, HTML riche NETTOYÉ au write. `null` ⇒ vide. */
  readonly missions?: string | null;
  /** Compétences par niveau (F0-b). OPTIONNEL. `null` ⇒ effacé. Clés ⊆ `availableLevels` (autorité API). */
  readonly competencesByLevel?: CompetencesByLevelDto | null;
}

/** Lecture d'un Profile (édition / preview). */
export interface ProfileView {
  readonly profileCode: string;
  readonly catalogVersionId: string;
  readonly libelle: string;
  readonly famille: string;
  readonly sousFamille: string;
  readonly description: string;
  readonly availableLevels: readonly string[];
  readonly state: ProfileStateDto;
  /** Contextes d'intervention (DBU §2.4 l.941). `null` tant que non renseigné (parité missions/prerequis). */
  readonly contextes: string | null;
  /** Environnements technologiques associés (DBU §2.4 l.941). `null` tant que non renseigné. */
  readonly environnements: string | null;
  /** Descriptif de missions-type (DBU §2.4 l.950/952). HTML riche nettoyé. `null` tant que non renseigné. */
  readonly missions: string | null;
  /** Compétences de référence PAR niveau (F0-b). `null` tant que non renseigné. */
  readonly competencesByLevel: CompetencesByLevelDto | null;
}

// --- Matrice de corrélation (DDD §3.4.6 CorrelationEdge) ---

/** État d'une arête de corrélation (parité enum back `CorrelationEdgeStateDto`, A-CATALOG §2.4). */
export type CorrelationEdgeStateDto = 'Active' | 'Archived';

/** Écriture d'une arête de corrélation dans une version DRAFT (upsert). Poids ∈ [0,1]. */
export interface EditCorrelationEdgeInputDto {
  readonly edgeId?: string;
  readonly profileFrom: string;
  readonly profileTo: string;
  /** Poids ∈ [0,1] (nombre, pas de fuite Decimal). Validé par l'API. */
  readonly weight: number;
}

/** Lecture d'une arête de corrélation. */
export interface CorrelationEdgeView {
  readonly edgeId: string;
  readonly catalogVersionId: string;
  readonly profileFrom: string;
  readonly profileTo: string;
  readonly weight: number;
  /**
   * Cycle de vie de l'arête (A-CATALOG §2.4). `Active` par défaut ; `Archived` = soft-archive
   * borné à la DRAFT (cascade quand un profil impliqué est archivé). Exposé en LECTURE ; l'API ne
   * filtre PAS — la matrice ACTIVE masque les arêtes `Archived` CÔTÉ FRONT (DBU §2.4 l.983).
   */
  readonly state: CorrelationEdgeStateDto;
}

/**
 * §2.4 (A0R-10) — une campagne EN COURS référençant un profil (avertissement d'archivage INFORMATIONNEL).
 * Miroir front de `ProfileCampaignUsageEntry` (API). Aucune PII (intitulé + état + dates campagne).
 */
export interface ProfileCampaignUsageEntry {
  readonly campaignId: string;
  readonly intitule: string;
  readonly state: string;
  readonly startDate: string | null;
  readonly endDate: string | null;
}

// --- A-25 compétences (SpecificSkill, DDD §3.4.4) ---

/** État d'une SpecificSkill (parité enum back `SkillStateDto`). */
export type SkillStateDto = 'Active' | 'Archived';

/** Création d'une SpecificSkill (A-25). `skillCode` alloué/vérifié par le service (inv.1). */
export interface CreateSkillInputDto {
  readonly skillCode: string;
  readonly libelle: string;
  readonly description: string;
  readonly categorie: string;
  /** Familles de profil associées (référence Profile.famille). */
  readonly familles: readonly string[];
}

/** Mise à jour partielle d'une SpecificSkill (libellé/description/catégorie/état). */
export interface UpdateSkillInputDto {
  readonly libelle?: string;
  readonly description?: string;
  readonly categorie?: string;
  readonly state?: SkillStateDto;
}

/** Lecture d'une SpecificSkill avec ses familles associées. */
export interface SkillView {
  readonly skillCode: string;
  readonly libelle: string;
  readonly description: string;
  readonly categorie: string;
  readonly state: SkillStateDto;
  /** ISO 8601. */
  readonly createdAt: string;
  /**
   * Date de DERNIÈRE MODIFICATION véridique (DBU §2.10 l.2204 ; F0-DB-4). ISO 8601. Affichée dans la
   * colonne « Date de dernière modification » (S210-AUDIT-MODIF #24) — plus de fallback `createdAt`.
   */
  readonly updatedAt: string;
  readonly familles: readonly string[];
}

// --- A-10 upload Excel dry-run (DDD §3.4.1/§3.4.3 ; G0-S9-4) ---

/** Statut de parsing (parité enum back `ParsingStatusDto`). */
export type ParsingStatusDto = 'Pending' | 'ParsingFailed' | 'ParsingOk';

/** Statut de validation sémantique (parité enum back `ValidationStatusDto`). */
export type ValidationStatusDto = 'NotStarted' | 'Failed' | 'Passed';

/** Issue d'une tentative d'upload (parité enum back `UploadOutcomeDto`). */
export type UploadOutcomeDto = 'Pending' | 'Cancelled' | 'PromotedToDraft';

/**
 * Anomalie de validation (DDD §3.4.4 `validation_report`). `row=null` ⇒ anomalie
 * globale. `severity=blocking` ⇒ promotion DRAFT interdite (inv.7).
 */
export interface UploadAnomalyDto {
  readonly code: string;
  readonly message: string;
  readonly row: number | null;
  readonly severity: 'blocking' | 'warning';
}

/** Bilan des changements détectés par le dry-run (compteurs par catégorie d'objet). */
export interface UploadChangeSummaryDto {
  readonly profilesAdded: number;
  readonly profilesModified: number;
  readonly profilesRemoved: number;
  readonly skillsAdded: number;
  readonly skillsModified: number;
  readonly skillsRemoved: number;
}

/**
 * Preview du dry-run d'upload (A-10) renvoyé à l'admin AVANT confirmation. AUCUNE
 * version produite à ce stade (`outcome=Pending`, G0-S9-4) ; l'admin décide ensuite.
 */
export interface UploadDryRunPreview {
  readonly attemptId: string;
  readonly sourceFilename: string;
  readonly parsingStatus: ParsingStatusDto;
  readonly validationStatus: ValidationStatusDto;
  readonly anomalies: readonly UploadAnomalyDto[];
  readonly changeSummary: UploadChangeSummaryDto;
  /** `true` si ≥ 1 anomalie bloquante : la promotion DRAFT est REFUSÉE (inv.7). */
  readonly hasBlockingAnomalies: boolean;
}

/** Résultat d'une confirmation d'upload (id de la DRAFT produite, G0-S9-5). */
export interface ConfirmUploadResult {
  readonly attemptId: string;
  readonly producedVersionId: string;
}

/**
 * Contrat du client catalog ADMIN d'ÉCRITURE — miroir des controllers S9-D2/D3
 * (CatalogEditionController, CatalogSkillController, CatalogUploadController). Aucune
 * règle métier ici : l'autorité (RBAC, inv.1/inv.2/inv.7, poids [0,1], dry-run sans
 * création de version) est l'API. L'UI relaie l'intention d'écriture ; l'erreur API
 * remonte telle quelle.
 */
export interface CatalogAdminEditionClient {
  // A-09 profil + matrice — @Controller('admin/catalog/versions/:catalogVersionId').
  /** GET .../profiles — liste les Profiles d'une version (preview édition). */
  listProfiles(catalogVersionId: string): Promise<readonly ProfileView[]>;
  /**
   * PUT .../profiles — upsert d'un Profile dans une version DRAFT. L'autorité (inv.1 unicité perpétuelle,
   * inv.2 édition réservée DRAFT, l.957 édition réservée aux profils ACTIFS) est l'API : une modification
   * d'un profil ARCHIVÉ est refusée en 409 `PROFILE_ARCHIVED_NOT_EDITABLE` (CatalogApiError, avec `code`).
   */
  upsertProfile(
    catalogVersionId: string,
    input: EditProfileInputDto,
  ): Promise<ProfileView>;
  /**
   * POST .../profiles/:profileCode/archive — ARCHIVE un Profile sur une version DRAFT (bascule
   * `state=Archived` + cascade ses arêtes de corrélation, A-CATALOG §2.4). L'autorité (R1 version
   * DRAFT, R3 idempotence) est l'API : 409 `CATALOG_VERSION_WRONG_STATE` (version ≠ DRAFT) ou
   * `PROFILE_ALREADY_ARCHIVED` (déjà archivé) remontent en `CatalogApiError` (avec `code`). Renvoie
   * la vue du Profile archivé (relu).
   */
  archiveProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView>;
  /**
   * POST .../profiles/:profileCode/restore — RESTAURE un Profile archivé (Archived→Active, DBU §2.4
   * l.960, DEC-8 CODE-ONLY : n'RÉactive PAS les arêtes). L'autorité (R1 version DRAFT, R3 profil archivé)
   * est l'API : 409 `CATALOG_VERSION_WRONG_STATE` / `PROFILE_NOT_ARCHIVED` remontent en `CatalogApiError`.
   */
  restoreProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView>;
  /**
   * POST .../profiles/:profileCode/clone — CLONE un Profile (code NEUF, contenu copié, 0 arête, DBU §2.4
   * l.950). Renvoie la vue du clone (state Active). 409 `CATALOG_VERSION_WRONG_STATE` si version ≠ DRAFT.
   */
  cloneProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView>;
  /**
   * POST .../profiles/:sourceProfileCode/merge — FUSIONNE le profil SOURCE dans le profil CIBLE (R15-CAT-01,
   * DBU §2.4 l.866) : transfère les corrélations source→cible + archive la source. `confirmationCode` DOIT
   * égaler `sourceProfileCode` (garde-fou textuel, parité promotion). L'autorité (R1 version DRAFT, source ≠
   * cible, confirmation, opérandes actifs) est l'API : 409/400 STRUCTURÉS (`CATALOG_VERSION_WRONG_STATE`,
   * `PROFILE_MERGE_SAME_PROFILE`, `PROFILE_MERGE_CONFIRMATION_MISMATCH`, `PROFILE_MERGE_OPERAND_ARCHIVED`)
   * remontent en `CatalogApiError` (avec `code`). Renvoie la vue du profil CIBLE (survivant, relu).
   */
  mergeProfiles(
    catalogVersionId: string,
    sourceProfileCode: string,
    targetProfileCode: string,
    confirmationCode: string,
  ): Promise<ProfileView>;
  /**
   * GET .../profiles/suggest-code — code de profil candidat AFFICHABLE (création assistée, DBU §2.4 l.952).
   * L'allocation RÉELLE reste au PUT (aucune réservation d'état).
   */
  suggestProfileCode(catalogVersionId: string): Promise<{ readonly profileCode: string }>;
  /** GET .../profiles/:profileCode — lit un Profile (visualiser, DBU §2.4 l.950). 404 si absent. */
  getProfile(catalogVersionId: string, profileCode: string): Promise<ProfileView>;
  /**
   * GET .../profiles/:profileCode/campaign-usage — campagnes EN COURS référençant le profil (avertissement
   * d'archivage §2.4). LECTURE SEULE, `[]` si aucune (jamais 404). INFORMATIONNEL : n'implique aucun blocage.
   */
  listProfileCampaignUsage(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<readonly ProfileCampaignUsageEntry[]>;
  /** GET .../correlation-edges — liste les arêtes de corrélation d'une version. */
  listEdges(catalogVersionId: string): Promise<readonly CorrelationEdgeView[]>;
  /** PUT .../correlation-edges — upsert d'une arête (poids ∈ [0,1]) dans une DRAFT. */
  upsertEdge(
    catalogVersionId: string,
    input: EditCorrelationEdgeInputDto,
  ): Promise<CorrelationEdgeView>;

  // A-08 promotion — @Controller('admin/catalog/versions/:catalogVersionId').
  /**
   * POST .../publish — promotion DRAFT→PUBLISHED (DBU §2.4). L'API archive ATOMIQUEMENT la PUBLISHED
   * précédente (« une seule PUBLISHED ») et émet DE.CatalogVersionPublished. Renvoie la vue de la
   * version publiée. L'autorité (transition légale, état requis DRAFT) est l'API : une version qui
   * n'est pas en Brouillon est refusée (409) et l'erreur remonte telle quelle.
   */
  publishVersion(catalogVersionId: string): Promise<CatalogVersionDto>;

  // A-25 compétences — @Controller('admin/catalog/skills').
  /** GET /admin/catalog/skills — liste les compétences (avec familles). */
  listSkills(): Promise<readonly SkillView[]>;
  /**
   * REM-A #1 (DBU §2.10) — GET /admin/catalog/skills/families : RÉFÉRENTIEL des familles de profils
   * associables (DISTINCT `Profile.famille`, toutes versions). Alimente le SÉLECTEUR contraint
   * d'association Compétence↔Familles (vs saisie texte libre). LECTURE SEULE, triée.
   */
  listAssociableFamilies(): Promise<readonly string[]>;
  /**
   * REM-A #3 (DBU §2.10 l.2274) — GET /admin/skills/matrix-export.csv : EXPORT CSV de la MATRICE
   * d'associations Compétences×Familles. Renvoie le CONTENU CSV (déjà durci côté API : neutralisation
   * d'injection de formule). LECTURE SEULE ; le front déclenche l'enregistrement navigateur du contenu.
   */
  exportSkillsMatrixCsv(): Promise<string>;
  /** POST /admin/catalog/skills — crée une compétence (inv.1) + DE.SkillCreated. */
  createSkill(input: CreateSkillInputDto): Promise<SkillView>;
  /** PATCH /admin/catalog/skills/:skillCode — MAJ partielle (libellé/description/catégorie). */
  updateSkill(skillCode: string, input: UpdateSkillInputDto): Promise<SkillView>;
  /** PUT /admin/catalog/skills/:skillCode/familles — réconcilie les familles associées. */
  setSkillFamilies(
    skillCode: string,
    familles: readonly string[],
  ): Promise<SkillView>;
  /** POST /admin/catalog/skills/:skillCode/archive — archive (state=Archived, ASSOCIATIONS préservées). */
  archiveSkill(skillCode: string): Promise<SkillView>;
  /**
   * POST /admin/catalog/skills/:skillCode/restore — RESTAURE une compétence archivée (Archivé → Actif,
   * associations préservées, DBU §2.10 l.2256 ; #16). Primitive DÉDIÉE (parité `restoreProfile`) : la
   * restauration NE passe PLUS par `updateSkill` (qui IGNORE `state` — NO-OP, bug #16). L'autorité (garde
   * inverse) est l'API : 409 `SKILL_NOT_ARCHIVED` si la compétence n'est pas archivée remonte en
   * `CatalogApiError` (avec `code`). Renvoie la vue de la compétence restaurée (state Active).
   */
  restoreSkill(skillCode: string): Promise<SkillView>;
  /**
   * R15-CAT-03 (DBU §2.10 l.2250 amendée DEC-27) — GET /admin/catalog/skills/:skillCode/campaign-usage :
   * campagnes EN COURS où la compétence est PROPOSABLE (avertissement d'archivage INFORMATIONNEL). Miroir
   * grain COMPÉTENCE de `listProfileCampaignUsage`. LECTURE SEULE, `[]` si aucune (jamais 404) ; n'implique
   * AUCUN blocage (une campagne fige sa version de catalogue). Réutilise le DTO `ProfileCampaignUsageEntry`.
   */
  listSkillCampaignUsage(
    skillCode: string,
  ): Promise<readonly ProfileCampaignUsageEntry[]>;

  // A-10 upload — @Controller('admin/catalog/uploads').
  /** POST /admin/catalog/uploads — upload + DRY-RUN (anomalies, SANS création de version). */
  uploadDryRun(file: File): Promise<UploadDryRunPreview>;
  /** POST /admin/catalog/uploads/:attemptId/confirm — promotion ATOMIQUE en DRAFT (G0-S9-5). */
  confirmUpload(
    attemptId: string,
    versionNumber: string,
  ): Promise<ConfirmUploadResult>;
  /** POST /admin/catalog/uploads/:attemptId/cancel — annule l'attempt (→ Cancelled). */
  cancelUpload(
    attemptId: string,
  ): Promise<{ readonly attemptId: string; readonly outcome: 'Cancelled' }>;
}

// --- Uc-CAT-PROFILS (fournisseur) — référentiel des profils de la PUBLISHED courante ---

/**
 * Fiche profil de LECTURE exposée au fournisseur (Uc-CAT-PROFILS). Miroir EXACT du DTO back
 * `ProfileDetailView` : libellé, famille/sous-famille, descriptif, niveaux disponibles, et —
 * quand ils existent — missions-type + prérequis. Aucune PII (catalog = référentiel GLOBAL).
 * `missions`/`prerequis` sont `null` tant qu'aucune source ne les peuple (rendus « non renseigné »).
 *
 * S34-FICHE-PROFONDEUR (#15, A0-8 OPTION 1 = V1) — `competencesByLevel`, `stacks` et `contextes` étendent la
 * fiche AU-DELÀ des seuls badges `availableLevels` (le faux-conforme #15). `availableLevels` est CONSERVÉ
 * (niveaux existants) ; `competencesByLevel` porte les compétences NOMMÉES par niveau (rendu structuré
 * Débutant/Maîtrisant/Expert/Référent). AUCUN versioning (A0-4 = V1.5). Clés ⊆ `availableLevels` (autorité API).
 */
export interface CatalogProfileDetailDto {
  readonly profileCode: string;
  readonly libelle: string;
  readonly famille: string;
  readonly sousFamille: string;
  readonly description: string;
  readonly availableLevels: readonly string[];
  readonly missions: string | null;
  readonly prerequis: string | null;
  /** Contextes d'intervention (DBU §3.4 l.2724). `null` tant que non renseigné (parité missions/prerequis). */
  readonly contextes: string | null;
  /**
   * Compétences de référence PAR NIVEAU (DBU §3.4 l.2724 ; F0-CONTRACT-3). `null` tant que non renseigné.
   * Clés ⊆ `availableLevels`. Rendu structuré par niveau dans la fiche (fin du faux-conforme #15).
   */
  readonly competencesByLevel: CompetencesByLevelDto | null;
  /**
   * « Stacks » technologiques du profil au grain FAMILLE (DBU §3.4 l.2726, cohérence ↔ §2.10). Dérivés des
   * SkillFamilyAssoc sur compétences ACTIVES. `[]` si la famille du profil ne porte aucune compétence active.
   */
  readonly stacks: readonly string[];
}

/**
 * Arête de corrélation entre deux profils exposée au fournisseur (Uc-CAT-PROFILS). Miroir EXACT du
 * back : couple orienté (from→to) + poids ∈ [0,1]. L'identité interne `edgeId` N'est PAS exposée
 * (minimalité) : le fournisseur n'a besoin que de la relation et de son intensité.
 */
export interface CatalogCorrelationDto {
  readonly profileFrom: string;
  readonly profileTo: string;
  readonly weight: number;
}

/**
 * Référentiel FOURNISSEUR de la version PUBLISHED courante (Uc-CAT-PROFILS). Miroir EXACT de la vue
 * back `SupplierCatalogReferentialView` : l'enveloppe de version (id + numéro + date), les profils et
 * les corrélations. `version === null` ⇔ aucune version publiée (listes alors VIDES). PUBLISHED-ONLY :
 * l'API résout l'UNIQUE version officielle — l'UI ne fournit ni ne lit jamais un id de version.
 */
export interface SupplierCatalogReferentialDto {
  readonly version: {
    readonly catalogVersionId: string;
    readonly versionNumber: string;
    readonly publishedAt: string | null;
  } | null;
  readonly profiles: readonly CatalogProfileDetailDto[];
  readonly correlations: readonly CatalogCorrelationDto[];
}

/**
 * R15-CMP-03 / F0-CONTRACT-4 — cible de saisie TJM résolue pour le fournisseur EN SESSION sur un profil.
 * Miroir EXACT du `TjmEntryTarget` back : couple d'identifiants opaques (aucune PII). Alimente le CTA
 * « Saisir mon TJM pour ce profil » et le deep-link `/saisie/$submissionId?profil=<code>`.
 */
export interface TjmEntryTargetDto {
  readonly campaignId: string;
  readonly submissionId: string;
}

/**
 * Contrat du client catalog FOURNISSEUR — miroir des endpoints de LECTURE F-05 / Uc-CAT-PROFILS.
 * Renvoie la version publiée courante (métadonnées), ou son référentiel complet (profils +
 * corrélations). L'autorité (PUBLISHED-only, RBAC supplier) est l'API ; l'UI relaie la lecture.
 */
export interface CatalogSupplierClient {
  /** GET /supplier/catalog/current — version publiée courante (F-05, gardé supplier). */
  getCurrentVersion(): Promise<CurrentCatalogVersionDto | null>;
  /**
   * GET /supplier/catalog/current/profiles — RÉFÉRENTIEL de la PUBLISHED courante (Uc-CAT-PROFILS,
   * gardé supplier). `version: null` + listes vides si aucune version publiée. Toujours un objet
   * (jamais `null` au niveau du résultat) : l'absence de version est portée par `version === null`.
   */
  listCurrentReferential(): Promise<SupplierCatalogReferentialDto>;
  /**
   * GET /supplier/catalog/tjm-entry-target/:profileCode — R15-CMP-03. Cible de saisie TJM du fournisseur
   * EN SESSION pour ce profil (F0-CONTRACT-4). `null` = aucune campagne ouverte où le fournisseur est
   * invité couvrant ce profil ⇒ PAS de CTA. Le siret vient de la session (cookie httpOnly), jamais du body.
   */
  resolveTjmEntryTarget(profileCode: string): Promise<TjmEntryTargetDto | null>;
}
